import React, { useState, useEffect } from 'react';
import { Modal, Text, Avatar, Box, Group, Loader } from '@mantine/core';
import { IconUser, IconCheck } from '@tabler/icons-react';
import '../../../../css/customer-attendance-modal.css';

interface CustomerAttendanceModalProps {
  opened: boolean;
  onClose: () => void;
  customer: {
    id: number;
    name: string;
    name_kana: string;
    photo_url?: string;
    avatar?: string;
    status: 'active' | 'paused' | 'withdrawn';
    notes?: string;
    store: {
      id: number;
      name: string;
    };
  } | null;
  attendance?: {
    action: 'check_in' | 'check_out';
    check_in_time?: string;
    check_out_time?: string;
    stay_duration?: string;
  };
}

export const CustomerAttendanceModal: React.FC<CustomerAttendanceModalProps> = ({
  opened,
  onClose,
  customer,
  attendance,
}) => {
  const [boxStates, setBoxStates] = useState({
    topRight: 'loading',
    bottomRight: 'loading',
    bottomLeft: 'loading',
    topLeft: 'loading',
  });
  const [allCompleted, setAllCompleted] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [countdownTimer, setCountdownTimer] = useState<NodeJS.Timeout | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  // localStorageから設定値を取得（デフォルト10秒）
  const getStoredCountdown = (): number => {
    const stored = localStorage.getItem('attendance_modal_countdown');
    return stored ? Math.max(5, Math.min(300, parseInt(stored, 10) || 10)) : 10;
  };

  const adjustCountdown = (delta: number) => {
    setCountdown((prev) => {
      if (prev === null) return null;
      const newVal = Math.max(5, Math.min(300, prev + delta));
      localStorage.setItem('attendance_modal_countdown', String(newVal));
      return newVal;
    });
  };

  useEffect(() => {
    if (opened) {
      // リセット（ボックスは最初非表示）
      setBoxStates({
        topRight: 'idle',
        bottomRight: 'idle',
        bottomLeft: 'idle',
        topLeft: 'idle',
      });
      setAllCompleted(false);
      setCountdown(null);
      setIsClosing(false);

      const timers: NodeJS.Timeout[] = [];

      // 右上ボックス: 0秒から0.3秒かけてフェードイン、フェードイン完了後にローディング開始
      timers.push(
        setTimeout(() => {
          setBoxStates((prev) => ({ ...prev, topRight: 'loading' }));
        }, 400)
      ); // 0.3秒フェードイン完了後

      timers.push(
        setTimeout(() => {
          setBoxStates((prev) => ({ ...prev, topRight: 'completed' }));
        }, 900)
      ); // 0.3秒 + 0.6秒

      // 右下ボックス: 0.3秒後に0.3秒かけてフェードイン、フェードイン完了後にローディング開始
      timers.push(
        setTimeout(() => {
          setBoxStates((prev) => ({ ...prev, bottomRight: 'loading' }));
        }, 700)
      ); // 0.3秒遅延 + 0.3秒フェードイン

      timers.push(
        setTimeout(() => {
          setBoxStates((prev) => ({ ...prev, bottomRight: 'completed' }));
        }, 1200)
      ); // 0.6秒 + 0.6秒

      // 左下ボックス: 0.6秒後に0.3秒かけてフェードイン、フェードイン完了後にローディング開始
      timers.push(
        setTimeout(() => {
          setBoxStates((prev) => ({ ...prev, bottomLeft: 'loading' }));
        }, 1000)
      ); // 0.6秒遅延 + 0.3秒フェードイン

      timers.push(
        setTimeout(() => {
          setBoxStates((prev) => ({ ...prev, bottomLeft: 'completed' }));
        }, 1500)
      ); // 0.9秒 + 0.6秒

      // 左上ボックス: 0.9秒後に0.3秒かけてフェードイン、フェードイン完了後にローディング開始
      timers.push(
        setTimeout(() => {
          setBoxStates((prev) => ({ ...prev, topLeft: 'loading' }));
        }, 1200)
      ); // 0.9秒遅延 + 0.3秒フェードイン

      timers.push(
        setTimeout(() => {
          setBoxStates((prev) => ({ ...prev, topLeft: 'completed' }));
        }, 1700)
      ); // 1.2秒 + 0.6秒

      // すべて完了後に背景色変更
      timers.push(
        setTimeout(() => {
          setAllCompleted(true);
          setCountdown(getStoredCountdown()); // カウントダウン開始
        }, 2400)
      );

      return () => {
        timers.forEach((timer) => clearTimeout(timer));
      };
    }
  }, [opened]);

  // フェードアウトしてからクローズ
  const handleClose = () => {
    // カウントダウンタイマーをクリア
    if (countdownTimer) {
      clearTimeout(countdownTimer);
      setCountdownTimer(null);
    }
    setCountdown(null);
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 500); // フェードアウトアニメーションの時間
  };

  // カウントダウンとオートクローズ
  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      setCountdownTimer(timer);
      return () => {
        clearTimeout(timer);
        setCountdownTimer(null);
      };
    } else if (countdown === 0) {
      handleClose();
    }
  }, [countdown]);

  if (!customer) return null;

  const actionColor = attendance?.action === 'check_in' ? 'green' : 'blue';
  const actionColorHex = attendance?.action === 'check_in' ? '#4ade80' : '#60a5fa';
  const actionBgColor =
    attendance?.action === 'check_in' ? 'rgba(74, 222, 128, 0.1)' : 'rgba(96, 165, 250, 0.25)';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      centered
      transitionProps={{ transition: 'fade', duration: 200 }}
      withCloseButton={false}
      padding={0}
      styles={{
        content: {
          background: 'transparent',
          border: 'none',
          boxShadow: 'none',
        },
        overlay: {
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          background: 'rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      <Box
        className={`modal-fade-in ${isClosing ? 'modal-fade-out' : ''}`}
        onClick={(e) => {
          // 透明部分をクリックした場合のみ閉じる
          if (e.target === e.currentTarget) {
            handleClose();
          }
        }}
        style={{
          position: 'relative',
          height: '100vh',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: allCompleted ? actionBgColor : 'transparent',
          transition: 'background-color 1s ease',
          cursor: 'pointer',
        }}
      >
        {/* ロゴを中心上部に配置 */}
        <img
          src="/public/images/s-logo.png"
          alt=""
          className="heartbeat-logo"
          style={{
            position: 'absolute',
            top: '50vh',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            height: '70vh',
            width: 'auto',
            opacity: '0.2',
            zIndex: -1,
          }}
        />

        {/* 中心にアバター画像 */}
        <Box
          className={
            allCompleted ? (attendance?.action === 'check_in' ? 'pulse-green' : 'pulse-blue') : ''
          }
          style={{
            position: 'absolute',
            top: '50vh',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
            borderRadius: '50%',
          }}
        >
          <Avatar
            src={customer.photo_url || undefined}
            alt={customer.name}
            size={280}
            radius="100%"
            style={{
              border: '3px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <IconUser size={140} />
          </Avatar>
        </Box>

        {/* 入退室メッセージ */}
        {allCompleted && (
          <Box
            className="message-fade-in"
            style={{
              position: 'absolute',
              top: '5vh',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 3,
              textAlign: 'center',
            }}
          >
            <Text
              size="xl"
              fw={700}
              style={{
                color: actionColorHex,
                fontSize: '48px',
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                letterSpacing: '2px',
              }}
            >
              {customer.name}様が{attendance?.action === 'check_in' ? '入室' : '退室'}いたしました
            </Text>
            {/* カウントダウン表示 */}
            {countdown !== null && (
              <Group gap="md" justify="center" style={{ marginTop: '20px' }}>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    adjustCountdown(-10);
                  }}
                  style={{
                    cursor: countdown <= 5 ? 'default' : 'pointer',
                    opacity: countdown <= 5 ? 0.3 : 0.8,
                    color: actionColorHex,
                    fontSize: '20px',
                    fontWeight: 700,
                    padding: '4px 12px',
                    borderRadius: '8px',
                    background: 'rgba(255,255,255,0.15)',
                    userSelect: 'none',
                  }}
                >
                  −10
                </Box>
                <Text
                  size="lg"
                  style={{
                    color: actionColorHex,
                    fontSize: '24px',
                    textShadow: '0 1px 5px rgba(0,0,0,0.3)',
                  }}
                >
                  自動的に閉じるまで: {countdown}秒
                </Text>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    adjustCountdown(10);
                  }}
                  style={{
                    cursor: countdown >= 300 ? 'default' : 'pointer',
                    opacity: countdown >= 300 ? 0.3 : 0.8,
                    color: actionColorHex,
                    fontSize: '20px',
                    fontWeight: 700,
                    padding: '4px 12px',
                    borderRadius: '8px',
                    background: 'rgba(255,255,255,0.15)',
                    userSelect: 'none',
                  }}
                >
                  +10
                </Box>
              </Group>
            )}
          </Box>
        )}

        {/* グラスモーフィズムボックス - 右上（顧客情報） */}
        <Box
          className={`box-fade-in-top-right ${boxStates.topRight === 'completed' ? 'box-complete' : ''}`}
          onClick={(e) => e.stopPropagation()}
          style={{
            cursor: 'default',
            position: 'absolute',
            top: '18vh',
            right: '18vw',
            width: '380px',
            height: '240px',
            background:
              boxStates.topRight === 'completed'
                ? attendance?.action === 'check_in'
                  ? 'rgba(74, 222, 128, 0.1)'
                  : 'rgba(96, 165, 250, 0.2)'
                : 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border:
              boxStates.topRight === 'completed'
                ? `1px solid ${actionColorHex}`
                : '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.5s ease',
          }}
        >
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '15px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
              paddingBottom: '8px',
            }}
          >
            {boxStates.topRight === 'idle' ? (
              <Box style={{ width: '20px', marginRight: '8px' }} />
            ) : boxStates.topRight === 'loading' ? (
              <Loader size="xs" color={actionColor} style={{ marginRight: '8px' }} />
            ) : (
              <IconCheck
                size={20}
                color={actionColorHex}
                style={{ marginRight: '8px', strokeWidth: 3 }}
              />
            )}
            <Text size="sm" fw={700} c="white">
              顧客情報
            </Text>
          </Box>
          <Box
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              paddingLeft: '25px',
            }}
          >
            <Box
              className={boxStates.topRight === 'completed' ? 'item-check-in' : ''}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                position: 'relative',
                padding: '2px 0',
              }}
            >
              {boxStates.topRight === 'completed' && (
                <IconCheck
                  size={14}
                  color={actionColorHex}
                  style={{
                    position: 'absolute',
                    left: '-25px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: 0,
                    animation: 'itemCheckIn 0.3s ease forwards',
                    animationDelay: '0.1s',
                  }}
                />
              )}
              <Text size="sm" c="white" style={{ opacity: 0.8 }}>
                ID
              </Text>
              <Text
                size="sm"
                c="white"
                fw={600}
                style={{
                  opacity: boxStates.topRight === 'completed' ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
              >
                #{customer.id}
              </Text>
            </Box>
            <Box
              className={boxStates.topRight === 'completed' ? 'item-check-in' : ''}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                position: 'relative',
                padding: '2px 0',
              }}
            >
              {boxStates.topRight === 'completed' && (
                <IconCheck
                  size={14}
                  color={actionColorHex}
                  style={{
                    position: 'absolute',
                    left: '-25px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: 0,
                    animation: 'itemCheckIn 0.3s ease forwards',
                    animationDelay: '0.3s',
                  }}
                />
              )}
              <Text size="sm" c="white" style={{ opacity: 0.8 }}>
                氏名
              </Text>
              <Text
                size="sm"
                c="white"
                fw={600}
                style={{
                  opacity: boxStates.topRight === 'completed' ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
              >
                {customer.name}
              </Text>
            </Box>
            <Box
              className={boxStates.topRight === 'completed' ? 'item-check-in' : ''}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                position: 'relative',
                padding: '2px 0',
              }}
            >
              {boxStates.topRight === 'completed' && (
                <IconCheck
                  size={14}
                  color={actionColorHex}
                  style={{
                    position: 'absolute',
                    left: '-25px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: 0,
                    animation: 'itemCheckIn 0.3s ease forwards',
                    animationDelay: '0.5s',
                  }}
                />
              )}
              <Text size="sm" c="white" style={{ opacity: 0.8 }}>
                店舗
              </Text>
              <Text
                size="sm"
                c="white"
                fw={600}
                style={{
                  opacity: boxStates.topRight === 'completed' ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
              >
                {customer.store?.name || '未設定'}
              </Text>
            </Box>
            <Box
              className={boxStates.topRight === 'completed' ? 'item-check-in' : ''}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'relative',
                padding: '2px 0',
              }}
            >
              {boxStates.topRight === 'completed' && (
                <IconCheck
                  size={14}
                  color={actionColorHex}
                  style={{
                    position: 'absolute',
                    left: '-25px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: 0,
                    animation: 'itemCheckIn 0.3s ease forwards',
                    animationDelay: '0.7s',
                  }}
                />
              )}
              <Text size="sm" c="white" style={{ opacity: 0.8 }}>
                ステータス
              </Text>
              <Box
                style={{
                  backgroundColor:
                    customer.status === 'active'
                      ? 'rgba(74, 222, 128, 0.2)'
                      : customer.status === 'paused'
                        ? 'rgba(251, 191, 36, 0.2)'
                        : 'rgba(156, 163, 175, 0.2)',
                  color:
                    customer.status === 'active'
                      ? '#4ade80'
                      : customer.status === 'paused'
                        ? '#fbbf24'
                        : '#9ca3af',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 600,
                  opacity: boxStates.topRight === 'completed' ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
              >
                {customer.status === 'active'
                  ? '在籍'
                  : customer.status === 'paused'
                    ? '休会'
                    : '退会'}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* グラスモーフィズムボックス - 右下（コース・支払い情報） */}
        <Box
          className={`box-fade-in-bottom-right ${boxStates.bottomRight === 'completed' ? 'box-complete' : ''}`}
          onClick={(e) => e.stopPropagation()}
          style={{
            cursor: 'default',
            position: 'absolute',
            bottom: '18vh',
            right: '18vw',
            width: '380px',
            height: '240px',
            background:
              boxStates.bottomRight === 'completed'
                ? attendance?.action === 'check_in'
                  ? 'rgba(74, 222, 128, 0.1)'
                  : 'rgba(96, 165, 250, 0.2)'
                : 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border:
              boxStates.bottomRight === 'completed'
                ? `1px solid ${actionColorHex}`
                : '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.5s ease',
          }}
        >
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '15px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
              paddingBottom: '8px',
            }}
          >
            {boxStates.bottomRight === 'idle' ? (
              <Box style={{ width: '20px', marginRight: '8px' }} />
            ) : boxStates.bottomRight === 'loading' ? (
              <Loader size="xs" color={actionColor} style={{ marginRight: '8px' }} />
            ) : (
              <IconCheck
                size={20}
                color={actionColorHex}
                style={{ marginRight: '8px', strokeWidth: 3 }}
              />
            )}
            <Text size="sm" fw={700} c="white">
              コース・支払い
            </Text>
          </Box>
          <Box
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              paddingLeft: '25px',
            }}
          >
            <Box
              className={boxStates.bottomRight === 'completed' ? 'item-check-in' : ''}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                position: 'relative',
                padding: '2px 0',
              }}
            >
              {boxStates.bottomRight === 'completed' && (
                <IconCheck
                  size={14}
                  color={actionColorHex}
                  style={{
                    position: 'absolute',
                    left: '-25px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: 0,
                    animation: 'itemCheckIn 0.3s ease forwards',
                    animationDelay: '0.1s',
                  }}
                />
              )}
              <Text size="sm" c="white" style={{ opacity: 0.8 }}>
                会員区分
              </Text>
              <Text
                size="sm"
                c="white"
                fw={600}
                style={{
                  opacity: boxStates.bottomRight === 'completed' ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
              >
                {customer.status === 'active'
                  ? '正会員'
                  : customer.status === 'paused'
                    ? '休会中'
                    : '退会'}
              </Text>
            </Box>
            <Box
              className={boxStates.bottomRight === 'completed' ? 'item-check-in' : ''}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                position: 'relative',
                padding: '2px 0',
              }}
            >
              {boxStates.bottomRight === 'completed' && (
                <IconCheck
                  size={14}
                  color={actionColorHex}
                  style={{
                    position: 'absolute',
                    left: '-25px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: 0,
                    animation: 'itemCheckIn 0.3s ease forwards',
                    animationDelay: '0.3s',
                  }}
                />
              )}
              <Text size="sm" c="white" style={{ opacity: 0.8 }}>
                月額料金
              </Text>
              <Text
                size="sm"
                c="white"
                fw={600}
                style={{
                  opacity: boxStates.bottomRight === 'completed' ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
              >
                ¥8,800
              </Text>
            </Box>
            <Box
              className={boxStates.bottomRight === 'completed' ? 'item-check-in' : ''}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'relative',
                padding: '2px 0',
              }}
            >
              {boxStates.bottomRight === 'completed' && (
                <IconCheck
                  size={14}
                  color={actionColorHex}
                  style={{
                    position: 'absolute',
                    left: '-25px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: 0,
                    animation: 'itemCheckIn 0.3s ease forwards',
                    animationDelay: '0.5s',
                  }}
                />
              )}
              <Text size="sm" c="white" style={{ opacity: 0.8 }}>
                支払状況
              </Text>
              <Box
                style={{
                  backgroundColor: 'rgba(74, 222, 128, 0.2)',
                  color: '#4ade80',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 600,
                  opacity: boxStates.bottomRight === 'completed' ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
              >
                正常
              </Box>
            </Box>
            <Box
              className={boxStates.bottomRight === 'completed' ? 'item-check-in' : ''}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                position: 'relative',
                padding: '2px 0',
              }}
            >
              {boxStates.bottomRight === 'completed' && (
                <IconCheck
                  size={14}
                  color={actionColorHex}
                  style={{
                    position: 'absolute',
                    left: '-25px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: 0,
                    animation: 'itemCheckIn 0.3s ease forwards',
                    animationDelay: '0.7s',
                  }}
                />
              )}
              <Text size="sm" c="white" style={{ opacity: 0.8 }}>
                次回請求
              </Text>
              <Text
                size="sm"
                c="white"
                fw={600}
                style={{
                  opacity: boxStates.bottomRight === 'completed' ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
              >
                2025年1月分
              </Text>
            </Box>
          </Box>
        </Box>

        {/* グラスモーフィズムボックス - 左上（入退室履歴） */}
        <Box
          className={`box-fade-in-top-left ${boxStates.topLeft === 'completed' ? 'box-complete' : ''}`}
          onClick={(e) => e.stopPropagation()}
          style={{
            cursor: 'default',
            position: 'absolute',
            top: '18vh',
            left: '18vw',
            width: '380px',
            height: '240px',
            background:
              boxStates.topLeft === 'completed'
                ? attendance?.action === 'check_in'
                  ? 'rgba(74, 222, 128, 0.1)'
                  : 'rgba(96, 165, 250, 0.2)'
                : 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border:
              boxStates.topLeft === 'completed'
                ? `1px solid ${actionColorHex}`
                : '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.5s ease',
          }}
        >
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '15px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
              paddingBottom: '8px',
            }}
          >
            {boxStates.topLeft === 'idle' ? (
              <Box style={{ width: '20px', marginRight: '8px' }} />
            ) : boxStates.topLeft === 'loading' ? (
              <Loader size="xs" color={actionColor} style={{ marginRight: '8px' }} />
            ) : (
              <IconCheck
                size={20}
                color={actionColorHex}
                style={{ marginRight: '8px', strokeWidth: 3 }}
              />
            )}
            <Text size="sm" fw={700} c="white">
              最近の入退室
            </Text>
          </Box>
          <Box
            style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              opacity: boxStates.topLeft === 'completed' ? 1 : 0,
              transition: 'opacity 0.3s ease',
            }}
          >
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '6px 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Text size="sm" c="white" style={{ opacity: 0.85 }}>
                12/27
              </Text>
              <Box style={{ display: 'flex', gap: '12px' }}>
                <Text size="sm" c="#4ade80">
                  入 16:30
                </Text>
                <Text size="sm" c="#60a5fa">
                  退 18:00
                </Text>
              </Box>
            </Box>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '6px 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Text size="sm" c="white" style={{ opacity: 0.85 }}>
                12/25
              </Text>
              <Box style={{ display: 'flex', gap: '12px' }}>
                <Text size="sm" c="#4ade80">
                  入 16:30
                </Text>
                <Text size="sm" c="#60a5fa">
                  退 18:00
                </Text>
              </Box>
            </Box>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '6px 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Text size="sm" c="white" style={{ opacity: 0.85 }}>
                12/23
              </Text>
              <Box style={{ display: 'flex', gap: '12px' }}>
                <Text size="sm" c="#4ade80">
                  入 16:30
                </Text>
                <Text size="sm" c="#60a5fa">
                  退 18:00
                </Text>
              </Box>
            </Box>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '6px 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Text size="sm" c="white" style={{ opacity: 0.85 }}>
                12/21
              </Text>
              <Box style={{ display: 'flex', gap: '12px' }}>
                <Text size="sm" c="#4ade80">
                  入 16:00
                </Text>
                <Text size="sm" c="#60a5fa">
                  退 17:30
                </Text>
              </Box>
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
              <Text size="sm" c="white" style={{ opacity: 0.85 }}>
                12/19
              </Text>
              <Box style={{ display: 'flex', gap: '12px' }}>
                <Text size="sm" c="#4ade80">
                  入 16:30
                </Text>
                <Text size="sm" c="#60a5fa">
                  退 18:00
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* グラスモーフィズムボックス - 左下（メモ・注意事項） */}
        <Box
          className={`box-fade-in-bottom-left ${boxStates.bottomLeft === 'completed' ? 'box-complete' : ''}`}
          onClick={(e) => e.stopPropagation()}
          style={{
            cursor: 'default',
            position: 'absolute',
            bottom: '18vh',
            left: '18vw',
            width: '380px',
            height: '240px',
            background:
              boxStates.bottomLeft === 'completed'
                ? attendance?.action === 'check_in'
                  ? 'rgba(74, 222, 128, 0.1)'
                  : 'rgba(96, 165, 250, 0.2)'
                : 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border:
              boxStates.bottomLeft === 'completed'
                ? `1px solid ${actionColorHex}`
                : '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.5s ease',
          }}
        >
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '15px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
              paddingBottom: '8px',
            }}
          >
            {boxStates.bottomLeft === 'idle' ? (
              <Box style={{ width: '20px', marginRight: '8px' }} />
            ) : boxStates.bottomLeft === 'loading' ? (
              <Loader size="xs" color={actionColor} style={{ marginRight: '8px' }} />
            ) : (
              <IconCheck
                size={20}
                color={actionColorHex}
                style={{ marginRight: '8px', strokeWidth: 3 }}
              />
            )}
            <Text size="sm" fw={700} c="white">
              メモ・注意事項
            </Text>
          </Box>
          <Box
            style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              opacity: boxStates.bottomLeft === 'completed' ? 1 : 0,
              transition: 'opacity 0.3s ease',
            }}
          >
            <Box
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                padding: '10px',
                borderRadius: '6px',
              }}
            >
              <Text size="sm" c="white" style={{ opacity: 0.8, marginBottom: '4px' }}>
                📝 メモ・注意事項
              </Text>
              <div
                style={{
                  fontSize: '14px',
                  color: 'white',
                  fontWeight: 600,
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                {customer.notes || 'なし'}
              </div>
            </Box>
          </Box>
        </Box>

        {/* 下部にカードデザイン */}
        <Box
          className={`card-rotate-drop ${attendance?.action === 'check_in' ? 'card-check-in' : 'card-check-out'}`}
          onClick={(e) => e.stopPropagation()}
          style={{
            cursor: 'default',
            position: 'absolute',
            bottom: '5vh',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '320px',
            height: '200px',
          }}
        >
          {/* カード背景画像 */}
          <img
            src="/public/images/card/card_f.png"
            alt=""
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: '12px',
            }}
          />

          {/* カード上のコンテンツ */}
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '15px 20px',
            }}
          >
            {/* タイトルを中心上部に配置 */}
            <Text
              size="xl"
              fw={700}
              style={{
                position: 'absolute',
                top: '20px',
                left: '40%',
                background:
                  'linear-gradient(90deg, #ff0080, #ff8c00, #ffd700, #00ff00, #00ffff, #0080ff, #ff00ff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Member ID Card
            </Text>

            {/* メインコンテンツ */}
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              {/* カード内左側に情報 */}
              <Box style={{ color: 'black', margin: '50px 0px 0px 50px' }}>
                <Text
                  style={{ marginBottom: '5px', fontSize: '9px', borderBottom: '1px solid gray' }}
                >
                  ID: {customer.id}
                </Text>

                <Text size="sm" fw={500} style={{ borderBottom: '1px solid gray' }}>
                  氏名: {customer.name}
                </Text>
              </Box>

              {/* カード内右側にアバター */}
              <Box style={{ marginTop: '50px' }}>
                <Avatar
                  src={customer.photo_url || undefined}
                  alt={customer.name}
                  size={100}
                  radius="100%"
                  style={{
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                  }}
                >
                  <IconUser size={40} />
                </Avatar>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
